import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { fetchTranslationProject, synthesizeAudio } from '../lib/translation/api';
import type { TranslationChapter } from '../lib/translation/api';

// A sliver of silent MP3. Playing it inside the user's click "unlocks" the
// shared <audio> element so later programmatic play() calls (after we've
// synthesised a segment) aren't blocked by the browser autoplay policy — which
// matters on mobile / the WebView shell where the gesture must reach play().
const UNLOCK_CLIP =
  'data:audio/mpeg;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjYyLjEyLjEwMQAAAAAAAAAAAAAA/+M4wAAAAAAAAAAAAEluZm8AAAAPAAAAAwAAAbAAqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqq1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV////////////////////////////////////////////AAAAAExhdmM2Mi4yOAAAAAAAAAAAAAAAACQC8AAAAAAAAAGw9wpEpwAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA/+MYxAAAAANIAAAAAExBTUUzLjEwMFVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV/+MYxDsAAANIAAAAAFVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV/+MYxHYAAANIAAAAAFVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV';

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
// How long to wait for on-demand synthesis before giving up on a segment.
const SYNTH_POLL_TRIES = 45;
const SYNTH_POLL_MS = 2000;

export type AudiobookStatus = 'idle' | 'buffering' | 'playing' | 'paused' | 'error';

interface FlatSeg {
  id: string;
  externalId: string;
  chapterIndex: number;
  audioUrl: string | null;
}

interface UseAudiobookArgs {
  projectId?: string;
  chapters: TranslationChapter[];
  // Switch the reader to a chapter (no-op when already there).
  goToChapter: (idx: number) => void;
  // Bring a segment into view in the reader.
  scrollToSegment: (externalId: string) => void;
  // The segment playback should begin from when the listener hits play
  // (typically the translated line at the top of the page). Null → from the
  // first translated line in the book.
  getStartKey: () => string | null;
}

export interface Audiobook {
  active: boolean;
  status: AudiobookStatus;
  // externalId of the segment currently playing/queued (drives the highlight).
  playingKey: string | null;
  // Playback position within the current segment, 0..1 (drives the approximate
  // sentence highlight). Reflects the seek target while still buffering.
  playingFraction: number;
  enter: () => void;
  exit: () => void;
  toggle: () => void;
  next: () => void;
  prev: () => void;
  // Start a line, optionally from a proportional offset (0..1) within it.
  seekTo: (externalId: string, fraction?: number) => void;
  // Whether a tapped line is part of the narration sequence (translated).
  isPlayable: (externalId: string) => boolean;
}

// Drives sequential narration playback over a project's translated segments:
// synthesises audio on demand, advances line by line and across chapters, and
// keeps a single gesture-unlocked <audio> element so playback isn't blocked.
export function useAudiobook({
  projectId,
  chapters,
  goToChapter,
  scrollToSegment,
  getStartKey,
}: UseAudiobookArgs): Audiobook {
  const qc = useQueryClient();
  const [active, setActive] = useState(false);
  const [status, setStatus] = useState<AudiobookStatus>('idle');
  const [playingKey, setPlayingKey] = useState<string | null>(null);
  const [playingFraction, setPlayingFraction] = useState(0);

  // Translated segments in reading order; only these are narrated.
  const flat = useMemo<FlatSeg[]>(() => {
    const list: FlatSeg[] = [];
    chapters.forEach((ch, ci) =>
      ch.segments.forEach((s) => {
        if (s.targetText)
          list.push({ id: s.id, externalId: s.externalId, chapterIndex: ci, audioUrl: s.audioUrl });
      })
    );
    return list;
  }, [chapters]);
  const indexByKey = useMemo(() => {
    const m = new Map<string, number>();
    flat.forEach((s, i) => m.set(s.externalId, i));
    return m;
  }, [flat]);

  // Latest snapshots, readable from the async playback controller.
  const flatRef = useRef(flat);
  const idxRef = useRef(indexByKey);
  useEffect(() => {
    flatRef.current = flat;
    idxRef.current = indexByKey;
  }, [flat, indexByKey]);

  const audioElRef = useRef<HTMLAudioElement | null>(null);
  const unlockedRef = useRef(false);
  // True only while a real segment (not the unlock clip) is loaded, so the
  // unlock clip's own 'ended'/'error' events don't drive the sequence.
  const realPlaybackRef = useRef(false);
  const endedRef = useRef<() => void>(() => {});
  // Lets the end-of-segment handler advance without forward-referencing the
  // (later-declared) playFlatIndex.
  const playRef = useRef<(fi: number) => void>(() => {});
  // Bumped on every (re)start so stale async resolves abort.
  const tokenRef = useRef(0);
  // Segment id → resolved blob URL, so re-seeking never re-synthesises.
  const resolvedRef = useRef(new Map<string, string>());
  // Where playback should begin within the current segment (0..1).
  const startFractionRef = useRef(0);

  const ensureEl = useCallback(() => {
    if (audioElRef.current) return audioElRef.current;
    const el = new Audio();
    el.preload = 'auto';
    el.addEventListener('playing', () => {
      if (realPlaybackRef.current) setStatus('playing');
    });
    el.addEventListener('ended', () => {
      if (realPlaybackRef.current) endedRef.current();
    });
    el.addEventListener('error', () => {
      if (realPlaybackRef.current) setStatus('error');
    });
    // Report progress so the reader can highlight the sentence being spoken.
    el.addEventListener('timeupdate', () => {
      if (!realPlaybackRef.current || !Number.isFinite(el.duration) || el.duration <= 0) return;
      setPlayingFraction(el.currentTime / el.duration);
    });
    audioElRef.current = el;
    return el;
  }, []);

  // Must run inside a user gesture. Plays a moment of silence so the browser
  // grants this element autoplay for the (async) real playback that follows.
  const unlock = useCallback(() => {
    const el = ensureEl();
    if (unlockedRef.current) return;
    unlockedRef.current = true;
    realPlaybackRef.current = false;
    try {
      el.src = UNLOCK_CLIP;
      el.play().catch(() => {});
    } catch {
      /* ignore */
    }
  }, [ensureEl]);

  // Resolve a playable URL, synthesising + polling when the segment has none.
  const resolveUrl = useCallback(
    async (seg: FlatSeg, token: number): Promise<string | null> => {
      const cached = resolvedRef.current.get(seg.id);
      if (cached) return cached;
      if (seg.audioUrl) {
        resolvedRef.current.set(seg.id, seg.audioUrl);
        return seg.audioUrl;
      }
      if (!projectId) return null;
      try {
        await synthesizeAudio(seg.id);
      } catch {
        /* fall through to polling; the job may still be queued */
      }
      for (let i = 0; i < SYNTH_POLL_TRIES && token === tokenRef.current; i += 1) {
        await sleep(SYNTH_POLL_MS);
        const fresh = await fetchTranslationProject(projectId);
        const found = fresh?.chapters.flatMap((c) => c.segments).find((x) => x.id === seg.id);
        if (found?.audioUrl) {
          resolvedRef.current.set(seg.id, found.audioUrl);
          qc.invalidateQueries({ queryKey: ['translationProject', projectId] });
          return found.audioUrl;
        }
        if (found?.audioStatus === 'audio_failed') return null;
      }
      return null;
    },
    [projectId, qc]
  );

  const playFlatIndex = useCallback(
    (fi: number, startFraction = 0) => {
      const list = flatRef.current;
      if (fi < 0 || fi >= list.length) return;
      const seg = list[fi];
      const token = (tokenRef.current += 1);
      const frac = Math.min(Math.max(startFraction, 0), 0.999);
      startFractionRef.current = frac;
      setPlayingKey(seg.externalId);
      setPlayingFraction(frac);
      setStatus('buffering');
      goToChapter(seg.chapterIndex);
      scrollToSegment(seg.externalId);

      // Warm the next line's audio so playback flows without a stall.
      const nxt = list[fi + 1];
      if (projectId && nxt && !nxt.audioUrl && !resolvedRef.current.has(nxt.id))
        synthesizeAudio(nxt.id).catch(() => {});

      (async () => {
        const url = await resolveUrl(seg, token);
        if (token !== tokenRef.current) return;
        const el = ensureEl();
        if (!url) {
          setStatus('error');
          return;
        }
        realPlaybackRef.current = true;
        endedRef.current = () => {
          const cur = idxRef.current.get(seg.externalId);
          if (cur === undefined) return;
          if (cur + 1 < flatRef.current.length) playRef.current(cur + 1);
          else setStatus('paused');
        };
        // Jump to the requested offset once the duration is known (and again
        // immediately, for an already-buffered clip the browser won't reload).
        const applyStart = () => {
          if (token !== tokenRef.current) return;
          if (frac > 0 && Number.isFinite(el.duration) && el.duration > 0)
            el.currentTime = frac * el.duration;
        };
        try {
          el.src = url;
          el.currentTime = 0;
          el.addEventListener('loadedmetadata', applyStart, { once: true });
          await el.play();
          applyStart();
        } catch {
          if (token === tokenRef.current) setStatus('error');
        }
      })();
    },
    [ensureEl, goToChapter, scrollToSegment, resolveUrl, projectId]
  );
  useEffect(() => {
    playRef.current = playFlatIndex;
  }, [playFlatIndex]);

  const enter = useCallback(() => {
    unlock();
    if (flatRef.current.length === 0) return;
    const startKey = getStartKey();
    const fi = startKey ? (idxRef.current.get(startKey) ?? -1) : -1;
    setActive(true);
    playFlatIndex(fi >= 0 ? fi : 0);
  }, [unlock, getStartKey, playFlatIndex]);

  const exit = useCallback(() => {
    tokenRef.current += 1;
    realPlaybackRef.current = false;
    audioElRef.current?.pause();
    setActive(false);
    setStatus('idle');
    setPlayingKey(null);
    setPlayingFraction(0);
  }, []);

  const toggle = useCallback(() => {
    const el = audioElRef.current;
    if (!el) return;
    if (status === 'playing') {
      el.pause();
      setStatus('paused');
    } else if (status === 'paused') {
      unlock();
      el.play()
        .then(() => setStatus('playing'))
        .catch(() => {});
    } else if (status === 'error' && playingKey) {
      const fi = idxRef.current.get(playingKey);
      if (fi !== undefined) playFlatIndex(fi);
    }
  }, [status, unlock, playingKey, playFlatIndex]);

  const step = useCallback(
    (delta: 1 | -1) => {
      unlock();
      if (!playingKey) return;
      const fi = idxRef.current.get(playingKey);
      if (fi === undefined) return;
      const target = fi + delta;
      if (target >= 0 && target < flatRef.current.length) playFlatIndex(target);
    },
    [unlock, playingKey, playFlatIndex]
  );
  const next = useCallback(() => step(1), [step]);
  const prev = useCallback(() => step(-1), [step]);

  const seekTo = useCallback(
    (key: string, fraction = 0) => {
      unlock();
      const fi = idxRef.current.get(key);
      if (fi !== undefined) playFlatIndex(fi, fraction);
    },
    [unlock, playFlatIndex]
  );

  const isPlayable = useCallback((key: string) => idxRef.current.has(key), []);

  // Stop playback if the reader unmounts.
  useEffect(
    () => () => {
      tokenRef.current += 1;
      audioElRef.current?.pause();
    },
    []
  );

  return {
    active,
    status,
    playingKey,
    playingFraction,
    enter,
    exit,
    toggle,
    next,
    prev,
    seekTo,
    isPlayable,
  };
}
